import { assert } from '@ember/debug';
import { A, isArray } from '@ember/array';
import { get, set } from '@ember/object';
import { isEmpty } from '@ember/utils';
import { defer, resolve } from 'rsvp';
import { task } from 'ember-concurrency';

const confirmTransferTask = () => {
  return task({
    destination: null,
    isTransferToNewLocation: false,

    *perform({ destination, origins = [] }) {
      const deferred = defer();

      const destinationLocationId = destination.belongsTo('location').id();

      const isTransferToNewLocation = origins.some((deliveryArea) => {
        return deliveryArea.belongsTo('location').id() !== destinationLocationId;
      });

      set(this, 'destination', destination);
      set(this, 'isTransferToNewLocation', isTransferToNewLocation);

      this.abort = () => deferred.resolve(false);
      this.continue = () => deferred.resolve(true);

      return yield deferred.promise;
    },
  });
};

const findAllDeliveryAreasForLocation = (store, location) => {
  return store.query('delivery-area', {
    filter: {
      location: get(location, 'id'),
    },
  });
};

const findAllDeliveryAreasForMultipleLocations = (store, locations) => {
  assert('The second argument should be an array', isArray(locations));
  const locationIds = locations.mapBy('id');

  if (isEmpty(locations)) {
    return resolve(A());
  }

  return store.query('delivery-area', {
    filter: {
      location: locationIds.join(','),
    },
  });
};

const findAllDeliveryAreasForCompany = (store) => {
  return store.findAll('delivery-area');
};

/**
 * Returns the delivery areas that count against the deliveries allotment.
 *
 * This is the single source of truth for "active delivery areas" across the
 * gating modal, the settings index, and the delivery-area form, so their counts
 * can't drift apart. Scope mirrors the `locationBilling` flag: when it's on we
 * only count the current location, otherwise we count the whole company.
 * Unsaved (`isNew`) records are always excluded, and only `enabled` areas count.
 *
 * @param {Array<DeliveryArea>}  deliveryAreas
 * @param {Object}               [options]
 * @param {Boolean}              [options.locationBilling]   whether the `locationBilling` FF is on
 * @param {String}               [options.currentLocationId] id of the current location
 * @returns {Array<DeliveryArea>}
 */
const activeDeliveryAreas = (deliveryAreas, { locationBilling, currentLocationId } = {}) => {
  return (deliveryAreas ?? []).filter((area) => {
    if (area.isNew || !area.enabled) return false;
    // Scope to the current location only under location billing. If the current
    // location isn't resolved yet, fall back to the company-wide count rather than
    // filtering everything out.
    if (locationBilling && currentLocationId) {
      return area.belongsTo('location').id() === currentLocationId;
    }
    return true;
  });
};

export {
  confirmTransferTask,
  findAllDeliveryAreasForLocation,
  findAllDeliveryAreasForCompany,
  findAllDeliveryAreasForMultipleLocations,
  activeDeliveryAreas,
};
